import React, { useEffect, FC } from 'react';
import _ from 'lodash';
import { Spinner } from 'design-system';
import { isSmallScreen } from '@scale/frontend-lib/util';
import { IUserJson } from '@scale/shared/types/user';
import InstructionDesktop from './instruction';
import InstructionMobile from './instructionSidePanel';
import StickyFooter from './components/stickyFooter';
import QuizPage from './quiz';
import {
  ProjectInstructionProvider,
  useProjectInstructionContext,
  InstructionV2ResProps,
} from './projectInstructionContext';
import { Flex, Text } from '@scale/scaleui-radix';
import Image from 'next/image';

const InstructionWrapper = () => {
  const { isLoading, openQuiz, components, isEmbedded, isMobileView, setIsMobileView } =
    useProjectInstructionContext();

  useEffect(() => {
    const handleResize = _.debounce(() => setIsMobileView(isSmallScreen()), 400, {
      leading: true,
      trailing: true,
    });

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  if (openQuiz) {
    return <QuizPage />;
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: '50vh' }}>
        <Spinner />
      </div>
    );
  }

  if (_.isEmpty(components)) {
    return (
      <Flex direction="column" align="center" justify="center" gap="2" py="8">
        <Image src="/static/images/unavailable.svg" width={100} height={100} alt="" />

        <Text size="6" weight="medium">
          No instructions found
        </Text>
        <Text size="2" color="neutral">
          There are no instructions available for this project.
        </Text>
      </Flex>
    );
  }
  return (
    <>
      {isMobileView ? <InstructionMobile /> : <InstructionDesktop />}
      {isEmbedded && <StickyFooter />}
    </>
  );
};

interface InstructionProps {
  isEmbedded?: boolean; // embedded here means not full page mode
  isMobileView?: boolean;
  user?: IUserJson;
  userIsCustomer?: boolean;
  isEdgeCase?: boolean;
  startTasks?: () => void;
  startButtonText?: string;
  isRapidPreview?: boolean; // customer facing instruction in rapid end. 1. it won't group nav types 2. no project name in title
  instructionV2Res?: InstructionV2ResProps;
  // If provided, don't use startTasks/startButtonText
  customStartButton?: React.Component;
  disableDiff?: boolean;
  projectId?: string;
  disableScrollOnMount?: boolean;
  doNotRenderChildrenSet?: Set<string>;
  disableSidePanel?: boolean;
}

const Instruction: FC<InstructionProps> = props => {
  return (
    <ProjectInstructionProvider {...props}>
      <InstructionWrapper />
    </ProjectInstructionProvider>
  );
};

export default Instruction;
